import { useMemo, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import NewsRow from '@/components/base/NewsRow';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { newsList, newsTags } from '@/mocks/newsList';
import { usePageMeta } from '@/hooks/usePageMeta';
import { Link } from 'react-router-dom';

const PAGE_SIZE = 6;

export default function NewsCategory() {
  usePageMeta(
    '漂亮的姐姐 - 影视资讯 - 最新行业动态与剧情解读',
    '漂亮的姐姐影视资讯分类页，提供最新影视行业新闻、剧集剧情介绍与幕后解读，支持多维标签筛选，让你快速掌握影视圈动态。',
  );

  const [tag, setTag] = useState('全部');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = tag === '全部' ? newsList : newsList.filter((item) => item.tags.includes(tag));
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [tag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const hotNews = [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 3);

  return (
    <div className="w-full max-w-page mx-auto px-4 md:px-6 py-6">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视资讯' }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">影视资讯</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
          聚合最新影视行业新闻、剧集剧情介绍与幕后解读，按发布日期倒序排列，可通过标签快速筛选你关心的话题。
        </p>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {newsTags.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => {
              setTag(item);
              setPage(1);
            }}
            className={`px-3.5 py-2 rounded-full text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
              tag === item
                ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            {item}
          </button>
        ))}
      </div>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <p className="text-sm text-foreground-600 mb-2">
            共找到 <strong className="text-primary-700">{filtered.length}</strong> 篇资讯
            {tag !== '全部' ? <span className="text-foreground-500">（标签：{tag}）</span> : null}
          </p>

          {pageItems.length > 0 ? (
            <>
              <div className="bg-background-50 border border-background-200 rounded-lg px-4 md:px-5">
                {pageItems.map((item) => (
                  <NewsRow key={item.id} item={item} />
                ))}
              </div>
              <Pagination current={safePage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              title="该标签下暂无资讯"
              description="换个标签看看，或先浏览下方的热门资讯推荐。"
              actionLabel="查看全部资讯"
              onAction={() => {
                setTag('全部');
                setPage(1);
              }}
            />
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden lg:sticky lg:top-20">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-background-200 bg-accent-50">
              <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                <i className="ri-fire-line"></i>
              </span>
              <h2 className="font-heading text-base font-bold text-foreground-950">热门资讯推荐</h2>
            </div>
            <div className="divide-y divide-background-200">
              {hotNews.map((item) => (
                <Link key={item.id} to={`/detail/news/${item.id}`} className="block p-4 hover:bg-background-100 transition-colors cursor-pointer">
                  <h3 className="font-heading text-sm font-bold text-foreground-900 line-clamp-2 hover:text-primary-600 transition-colors">
                    {item.title}
                  </h3>
                  <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-calendar-line"></i>
                      {item.date}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-label whitespace-nowrap">{item.tags[0]}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}